import { PlayerSwap } from "@/api/privateRequests/sbcs/player-swap";
import pitch from "@/assets/pitch.png";
import { pitchCardPlaceholder } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { PlayerSwapPopover } from "@/pages/Sbcs/components/PlayerSwapPopover";
import { Sbc } from "@/redux/sbcs/sbcsSlice";
import { Player } from "@/types/SBCPlayer";
import { type FormationSlot, formationSlots } from "@repo/shared/utils";
import React, { ReactNode } from "react";

import {
  LockedPlayerCard,
  PITCH_CARD_SCALE,
  SolutionPlayerCard,
  isLockedPlayer,
  playerSource,
  toCardPlayer,
} from "./SolutionPlayerCard";
import { SolutionPlayerFacts } from "./SolutionPlayerFacts";

/* While a solve is in flight the pitch renders empty slots, so a slot holds
   either a player or a stand-in for one. */
/*
  A slot's place on the pitch. The top moves on a wide screen, where the pitch
  is shorter for its width; webapp-formations.css picks the value.
*/
const slotStyle = (
  slots: readonly FormationSlot[],
  index: number,
): React.CSSProperties => {
  const slot = slots[index];
  if (!slot) return {};
  return {
    left: slot.left,
    ["--slot-top" as string]: slot.top,
    ["--slot-top-wide" as string]: slot.wideTop ?? slot.top,
  };
};

type PitchPlaceholder = { placeholder: true };
type PitchSlot = Player | PitchPlaceholder;

const isPlaceholder = (slot: PitchSlot): slot is PitchPlaceholder =>
  "placeholder" in slot;

/*
  Card size per breakpoint, relative to the old 140px pitch card. Applied as
  the card's own scale rather than a transform on the slot, so the layout box
  matches what is drawn and the slot offsets are computed from a real card.
*/
const CARD_SCALE = {
  /* Four across on a 320px pitch, with the chip under each. */
  mobile: 0.62,
  touch: 0.62,
  smallDesktop: 0.75,
  desktop: 0.85,
};

type PitchViewProps = {
  sbc: Sbc;
  sbcFormation?: string;
  playersToRender: PitchSlot[];
  isMobile: boolean;
  isTouch: boolean;
  smallDesktop: boolean;
  /** The figures sit above the pitch, so the pitch fills the viewport. */
  stacked: boolean;
  widthClass: string;
  playerSwaps: PlayerSwap[][];
  /** The swap request is in flight: every card's badge spins. */
  loadingSwaps?: boolean;
  onSwapClicked: (p: PlayerSwap, index: number) => void;
  /** Floated over the centre of the pitch, e.g. the solve status. */
  overlay?: ReactNode;
};

export const PitchView: React.FC<PitchViewProps> = ({
  sbcFormation,
  playersToRender,
  isMobile,
  isTouch,
  smallDesktop,
  stacked,
  widthClass,
  playerSwaps,
  loadingSwaps = false,
  onSwapClicked,
  sbc,
  overlay,
}) => {
  // Each slot's place, from the formation map both apps share.
  const slots = formationSlots(sbcFormation);
  const cardScale = isMobile
    ? CARD_SCALE.mobile
    : isTouch
      ? CARD_SCALE.touch
      : smallDesktop
        ? CARD_SCALE.smallDesktop
        : CARD_SCALE.desktop;

  return (
    <div
      className={cn(
        /*
        The artwork is 852x710, so this ratio crops nothing. Width-driven
        because slots are percentages of this box: the box is the scale the
        squad is read at. The margin clears the front row's upward translate.
      */
        "relative mx-auto mt-9 w-full max-w-[1020px] aspect-[120/100]",
        stacked && "h-full max-h-none min-h-0 aspect-auto",
      )}
    >
      <div
        className="absolute inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{ backgroundImage: `url(${pitch})` }}
        aria-hidden
      />

      <div
        className="absolute inset-0 ut-squad-pitch-view"
        style={{ ["--slot-offset-y" as string]: "-90%" }}
      >
        {playersToRender.map((slot, index: number) => {
          if (!sbc) return null;

          if (isPlaceholder(slot)) {
            return (
              <div
                key={`ph-${index}`}
                /* Each slot a beat behind the last, so the pulse runs across
                   the squad instead of blinking as one. */
                style={{
                  ...slotStyle(slots, index),
                  animationDelay: `${index * 80}ms`,
                }}
                className={cn(
                  `absolute ut-squad-slot-view-${index} -translate-x-1/2 z-20 animate-pulse text-dark-disabled`,
                  isMobile
                    ? "min-h-[calc(15%)]"
                    : isTouch
                      ? "min-h-[calc(20%)]"
                      : smallDesktop
                        ? "min-h-[calc(15%)]"
                        : "min-h-[calc(20%)]",
                  widthClass,
                  "aspect-[111/154] shrink-0",
                )}
              >
                {pitchCardPlaceholder}
              </div>
            );
          }

          const player = slot;
          const swaps = playerSwaps[index] ?? [];

          /* Nothing to open or swap on a slot the solver left empty. */
          if (isLockedPlayer(player)) {
            return (
              <div
                key={`locked-${index}`}
                className={`absolute ut-squad-slot-view-${index} -translate-x-1/2 z-10`}
                style={slotStyle(slots, index)}
              >
                <LockedPlayerCard scale={PITCH_CARD_SCALE * cardScale} />
              </div>
            );
          }

          /* Every card opens its facts; the swap column and the badge on the
             card only show when there is something to swap for. */
          return (
            <div
              key={player.id ?? `slot-${index}`}
              className={`absolute ut-squad-slot-view-${index} -translate-x-1/2 z-10`}
              style={slotStyle(slots, index)}
            >
              <PlayerSwapPopover
                facts={<SolutionPlayerFacts player={player} />}
                options={swaps.map((swap) => ({
                  player: toCardPlayer(swap),
                  source: playerSource(swap),
                  untradeable: swap.untradeable,
                }))}
                onSelect={(swapIndex) => onSwapClicked(swaps[swapIndex], index)}
              >
                <SolutionPlayerCard
                  player={player}
                  scale={PITCH_CARD_SCALE * cardScale}
                  hasSwaps={swaps.length > 0}
                  swapsLoading={loadingSwaps}
                />
              </PlayerSwapPopover>
            </div>
          );
        })}
      </div>

      {/* Held a fifth of the way down rather than dead centre: the centre
          circle and the midfield are where the eye lands anyway. */}
      {overlay && (
        <div className="pointer-events-none absolute inset-x-0 top-[20%] z-30 flex justify-center px-4">
          <div className="pointer-events-auto">{overlay}</div>
        </div>
      )}
    </div>
  );
};
